import { useQueryClient } from '@tanstack/react-query';
import { Building2, MoreHorizontal, Pencil, Plus, Power, PowerOff } from 'lucide-react';
import { useState } from 'react';

import {
  getAmbientesControllerListarQueryKey,
  useAmbientesControllerDefinirAtivo,
  useAmbientesControllerListar,
} from '@/api/generated/admin-ambientes/admin-ambientes';
import type { AmbienteResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { AmbienteFormDialogo } from '@/features/admin/ambientes/components/AmbienteFormDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Ambientes (F6-5).
 *
 * ⚠️ Não existe excluir, e isso é proposital: `tenants` tem 19 relações em
 * cascata — apagar um ambiente apagaria a clínica inteira, sem volta. A
 * operação equivalente é desativar.
 */
export function AmbientesPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState<string>(TODOS);
  const [situacao, setSituacao] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<AmbienteResponse | undefined>();
  const [aDesativar, setADesativar] = useState<AmbienteResponse | null>(null);

  const { data, isPending, isError, refetch } = useAmbientesControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nome',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(tipo !== TODOS ? { tipo: tipo as 'INDIVIDUAL' | 'PARCEIRO' } : {}),
    ...(situacao !== TODOS ? { ativo: situacao as 'true' | 'false' } : {}),
  });

  const definirAtivo = useAmbientesControllerDefinirAtivo();

  const podeCriar = pode('admin_tenants.criar');
  const podeEditar = pode('admin_tenants.editar');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function alternarAtivo(ambiente: AmbienteResponse, ativo: boolean) {
    try {
      await definirAtivo.mutateAsync({ id: ambiente.id, data: { ativo } });
      await queryClient.invalidateQueries({ queryKey: getAmbientesControllerListarQueryKey() });
      toast.success(ativo ? `"${ambiente.nome}" reativado.` : `"${ambiente.nome}" desativado.`);
    } catch (erro) {
      toast.error(
        erro instanceof ApiError ? erro.message : 'Não foi possível alterar o ambiente.',
      );
    } finally {
      setADesativar(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por nome ou identificador..."
            className="sm:max-w-xs"
          />

          <Select value={tipo} onValueChange={(v) => aoFiltrar(() => setTipo(v))}>
            <SelectTrigger className="sm:w-40" aria-label="Filtrar por tipo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os tipos</SelectItem>
              <SelectItem value="INDIVIDUAL">Individual</SelectItem>
              <SelectItem value="PARCEIRO">Parceiro</SelectItem>
            </SelectContent>
          </Select>

          <Select value={situacao} onValueChange={(v) => aoFiltrar(() => setSituacao(v))}>
            <SelectTrigger className="sm:w-40" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              <SelectItem value="true">Ativos</SelectItem>
              <SelectItem value="false">Desativados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {podeCriar && (
          <Button
            onClick={() => {
              setEmEdicao(undefined);
              setFormAberto(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Novo ambiente
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Ambientes</h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Building2}
            titulo="Nenhum ambiente encontrado"
            descricao={busca ? 'Tente outro termo de busca.' : 'Crie o primeiro ambiente.'}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ambiente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Membros</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((ambiente) => (
                  <TableRow key={ambiente.id}>
                    <TableCell>
                      <span className="font-medium text-foreground">{ambiente.nome}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">{ambiente.slug}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ambiente.tipo === 'PARCEIRO' ? 'default' : 'outline'}>
                        {ambiente.tipo === 'PARCEIRO' ? 'Parceiro' : 'Individual'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{ambiente.totalMembros}</TableCell>
                    <TableCell>
                      {ambiente.ativo ? (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-600/40">
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-destructive border-destructive/40">
                          Desativado
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Ações de ${ambiente.nome}`}
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {podeEditar && (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  setEmEdicao(ambiente);
                                  setFormAberto(true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                                Renomear
                              </DropdownMenuItem>

                              {ambiente.ativo ? (
                                <DropdownMenuItem
                                  onClick={() => setADesativar(ambiente)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <PowerOff className="w-4 h-4" />
                                  Desativar
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => void alternarAtivo(ambiente, true)}
                                >
                                  <Power className="w-4 h-4" />
                                  Reativar
                                </DropdownMenuItem>
                              )}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <AmbienteFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { ambiente: emEdicao } : {})}
      />

      {/* ⚠️ O aviso cita o número de membros de propósito: desativar tem efeito
          imediato e total, e sem o número a ação parece reversível-e-inofensiva
          quando na prática derruba a clínica inteira agora. */}
      <AlertDialog open={Boolean(aDesativar)} onOpenChange={(v) => !v && setADesativar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar "{aDesativar?.nome}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {aDesativar?.totalMembros === 0 ? (
                <>Este ambiente não tem membros ativos. Ele pode ser reativado a qualquer momento.</>
              ) : (
                <>
                  <strong>
                    {aDesativar?.totalMembros}{' '}
                    {aDesativar?.totalMembros === 1 ? 'usuário perde' : 'usuários perdem'} o acesso
                    imediatamente
                  </strong>{' '}
                  — na próxima ação que fizerem, sem precisar sair e entrar de novo. Nenhum dado é
                  apagado, e reativar devolve o acesso.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => aDesativar && void alternarAtivo(aDesativar, false)}
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
